import type { VoidFunctionComponent, ReactNode, RefObject, MouseEvent, ReactInstance } from 'react';
import { useState } from 'react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import MUIPopper from '@mui/material/Popper';
import type { PopperPlacementType } from '@mui/material/Popper';
import { IconButton } from '../Button';
import { icons } from '../Icon';
import type { IconType } from '../Icon';

type PopperProps = {
  children: ReactNode | ReactNode[];
  icon?: IconType;
  placement?: PopperPlacementType;
  modalContainer?: RefObject<HTMLElement>;
  isDisabled?: boolean;
};

/**
 * Reusable "Popper" component, useful for making small actionable
 * items appear after clicking something (usually an icon button).
 *
 * @param {PopperProps} props - Popper props
 * @param {ReactNode | ReactNode[]} props.children - Item(s) to appear in Popper.
 * @param {IconType} [props.icon] - Icon name to use (used like `icons[yourIconName]`). Defaults to "ellipsisHorizontal".
 * @param {PopperPlacementType} [props.placement] - Optionally control the placement of the Popper.
 * @param {RefObject<HTMLElement>} [props.modalContainer] - Ref to the modal container, which is required for Poppers to work on a modal (due to z-index issues).
 */
const Popper: VoidFunctionComponent<PopperProps> = ({
  children,
  icon = 'ellipsisVertical',
  placement = 'bottom-end',
  modalContainer,
  isDisabled,
}: PopperProps) => {
  const [anchorElement, setAnchorElement] = useState<null | HTMLElement>(null);

  //  Handle what happens when the target/button is clicked.
  const onTargetClick = (event: MouseEvent<HTMLElement>) =>
    setAnchorElement(anchorElement ? null : event.currentTarget);

  //  Handle what happens when the user clicks away from the Popper.
  const onClickAway = (event: MouseEvent<Document, MouseEvent>) => {
    if (!anchorElement?.contains(event.target as Node)) {
      setAnchorElement(null);
    }
  };

  return (
    <ClickAwayListener onClickAway={onClickAway}>
      <span className="inline-block">
        <IconButton
          icon={icons[icon]}
          onClick={onTargetClick}
          isDisabled={isDisabled}
        />
        <MUIPopper
          open={!!anchorElement}
          anchorEl={anchorElement}
          placement={placement}
          container={modalContainer?.current as (ReactInstance | undefined)}
        >
          {children}
        </MUIPopper>
      </span>
    </ClickAwayListener>
  );
};

export default Popper;
